import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  CheckCircle2,
  Package,
  Truck,
  Calendar,
  User,
  MapPin,
  Trash2,
  RotateCcw,
  ArrowLeft,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { motion, AnimatePresence } from "framer-motion";

const COMPLETED_STATUSES = ["Complete", "Shipped"];

export function CompletedOrdersPage() {
  const { user } = useAuth();
  const canEdit = user && (user.role === "admin" || user.role === "accountant");
  const isAdmin = user && user.role === "admin";
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [ordersData, statusesData] = await Promise.all([
        api.get("/orders?status=completed"),
        api.get("/config/order-statuses"),
      ]);
      setOrders(ordersData);
      setStatuses(statusesData);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (orderId) => {
    try {
      await api.delete(`/orders/${orderId}`);
      toast.success("Order deleted");
      fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to delete order");
    }
  };

  const handleReopen = async (orderId) => {
    const activeStatuses = statuses.filter((s) => !COMPLETED_STATUSES.includes(s.name));
    const firstStatus = activeStatuses[0]?.name || "Waiting";
    try {
      await api.put(`/orders/${orderId}`, { status: firstStatus });
      toast.success(`Order moved back to ${firstStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to reopen order");
    }
  };

  const shippedOrders = orders.filter((o) => o.status === "Shipped");
  const doneOrders = orders.filter((o) => o.status === "Complete");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse space-y-4 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const OrderItem = ({ order }) => (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="win-raised p-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <span className="font-medium text-foreground truncate">{order.customer_name}</span>
            {order.origin === "printhq" && (
              <span
                className="win-well px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground flex-shrink-0"
                title="Owned by PrintHQ"
              >
                {order.order_number || "PrintHQ"}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
              {order.source}
            </span>
            {/* PrintHQ raises most orders off a paid invoice, with no due date at all. */}
            {order.due_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
                {formatDate(order.due_date)}
              </span>
            )}
          </div>
          {order.notes && (
            <p className="text-sm text-muted-foreground/70 mt-2 line-clamp-2">{order.notes}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge className="capitalize">
            {order.status}
          </Badge>
          <div className="flex items-center gap-1">
            {canEdit && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-primary"
                onClick={() => handleReopen(order.id)}
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            )}
            {isAdmin && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Order</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this order?{" "}
                      {order.origin === "printhq"
                        ? "This order is owned by PrintHQ, so it will be deleted there too. This action cannot be undone."
                        : "This action cannot be undone."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDelete(order.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/orders")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">Completed Orders</h1>
          <p className="text-muted-foreground mt-1">Archive of shipped and completed commissions</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 win-well flex items-center justify-center">
                <Truck className="w-6 h-6 text-green-500" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Shipped</p>
                <p className="text-2xl font-bold font-mono">{shippedOrders.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 win-well flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Complete</p>
                <p className="text-2xl font-bold font-mono">{doneOrders.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders Lists */}
      <div className="space-y-6">
        {/* Shipped */}
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-green-500" strokeWidth={1.5} />
              Shipped Orders
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {shippedOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Truck className="w-12 h-12 mx-auto mb-3 opacity-50" strokeWidth={1} />
                <p>No shipped orders yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                <AnimatePresence mode="popLayout">
                  {shippedOrders.map((order) => (
                    <OrderItem key={order.id} order={order} />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Complete */}
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Complete Orders
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {doneOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" strokeWidth={1} />
                <p>No completed orders yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                <AnimatePresence mode="popLayout">
                  {doneOrders.map((order) => (
                    <OrderItem key={order.id} order={order} />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
